// Builds a link to the Google Maps *website* (not the billable Places API) so
// the user can quickly open a place while reviewing it. Prefers the canonical
// place link when a placeId is available, then coordinates, then a text search.

interface MapsLinkInput {
  name?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  zone?: string | null;
  destination?: string | null;
}

export function buildGoogleMapsUrl(input: MapsLinkInput): string | null {
  const { name, placeId, latitude, longitude, address, zone, destination } = input;

  if (placeId) {
    const query = encodeURIComponent(name || 'place');
    return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${placeId}`;
  }

  // Prefer a name-based text search over raw coordinates: Google resolves the
  // text to the actual establishment (with its card, photos and reviews),
  // whereas coordinates just drop a generic pin. Add zone/destination to
  // disambiguate a same-named place elsewhere. Fall back to the address, and
  // only use coordinates when there's nothing to search by.
  const named = [name, zone, destination].filter(Boolean).join(' ').trim();
  const text = named || (address ?? '').trim();
  if (text) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
  }

  if (latitude != null && longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  return null;
}
